import React from 'react';
import clsx from 'clsx';
import { Box } from '@mui/material';
import { FlexSpacer } from '../../Layout/components/FlexSpacer';
import { useStyles } from './EventItem.styles';
import { useEventDuration } from './EventItem.helpers';


export type EventItemVariant = 'appointment' | 'appointmentWarning' | 'appointmentOverlapWarning' | 'availability' | 'availabilityWarning' | 'ics';
export type EventItemSize = 'auto' | 'default' | 'compact';


export type EventItemProps = {
  title?: string;
  startTime: Date | null;
  endTime: Date | null;
  timezone: string;
  isDayView?: boolean;
  className?: string;
  size?: EventItemSize;
  elementRef?: React.Ref<HTMLDivElement>;
  id?: string;
  style?: React.CSSProperties;
  showFullRange?: boolean;
  leftSpacing?: boolean;
  elevation?: boolean;
  allDay?: boolean;

  isStart?: boolean;
  isEnd?: boolean;

  variant?: EventItemVariant;
};


export const EventItem: React.FC<EventItemProps> = ({ variant = 'appointment', ...props }) => {
  const { startTime, endTime, duration } = useEventDuration(props.startTime, props.endTime, props.timezone);

  const startTimeFormat = React.useMemo(() => {
    if (!startTime) return '';

    if (startTime.minute() === 0) {
      return startTime.format('h A');
    }
    return startTime.format('h:mm A');
  }, [ startTime ]);

  const endTimeFormat = React.useMemo(() => {
    if (!endTime) return '';
    if (endTime.minute() === 0) {
      return endTime.format('h A');
    }
    return endTime.format('h:mm A');
  }, [ endTime ]);

  const timeTitle = React.useMemo(() => {
    if (!startTimeFormat || !endTimeFormat) {
      return '';
    }

    if (props.showFullRange) {
      return `${startTimeFormat} - ${endTimeFormat}`;
    }
    return startTimeFormat;
  }, [ startTimeFormat, endTimeFormat, props.showFullRange ]);

  const size = React.useMemo<EventItemSize | undefined>(() => {
    if (!startTime || !endTime) {
      return 'default';
    }

    if (props.size) {
      return props.size;
    }

    if (props.isStart === true && props.isEnd === false) {
      const duration = endTime.startOf('day').diff(startTime, 'minutes');

      return duration <= 30 ? 'compact' : 'default';
    }
    if (props.isEnd === true && props.isStart === false) {
      const duration = endTime.diff(endTime.startOf('day'), 'minutes');

      return duration <= 30 ? 'compact' : 'default';
    }

    return props.size;
  }, [ props.isStart, props.isEnd, startTime, endTime, props.size ]);

  const compact = React.useMemo(() => {
    if (size === 'compact') {
      return true;
    } else if (size === 'default') {
      return false;
    }

    return duration <= 30;
  }, [ duration, size ]);

  const { classes } = useStyles({
    compact
  });

  const variantMapping: Record<EventItemVariant, string> = {
    appointment: classes.variantAppointment,
    appointmentWarning: classes.variantAppointmentWarning,
    appointmentOverlapWarning: classes.variantAppointmentOverlapWarning,
    availability: classes.variantAvailability,
    availabilityWarning: classes.variantAvailabilityWarning,
    ics: classes.variantIcs,
  };

  const rootClassNames = React.useMemo(() => {
    const result = [ classes.container, props.className ];

    if (variant) {
      result.push(variantMapping[variant]);
    }

    if (props.elevation) {
      if (props.allDay) {
        result.push(classes.elevationLow);
      } else {
        result.push(classes.elevation);
      }
    }

    if (props.isStart === false) {
      result.push(classes.noTopBorderRadius);
    }

    if (props.isEnd === false) {
      result.push(classes.noBottomBorderRadius);
    }

    return clsx(result);
  }, [ classes, variant, props.className, props.elevation, props.isStart, props.isEnd ]); // eslint-disable-line react-hooks/exhaustive-deps

  const finalStyles = React.useMemo(() => ({
    marginLeft: props.leftSpacing ? 5 : undefined,
    ...props.style,
  }), [ props.style, props.leftSpacing ]);

  return (
    <div
      ref={props.elementRef}
      style={finalStyles}
      className={rootClassNames}
      id={props?.id}
      data-name={props.title}
      data-start-time={startTime?.format()}
      title={props.title}
    >
      <FlexSpacer maxHeight="8px" />
      <Box display="flex">
        <Box width="100%" overflow="hidden">
          {!props.allDay && (
            <Box className={classes.startTimeWrapper}>
              <Box display="flex" overflow="hidden">
                <Box className={classes.startTime}>{timeTitle}</Box>
                {compact && props.title && <Box className={classes.timeUsername}>, {props.title}</Box>}
              </Box>
            </Box>
          )}
          {!compact && <Box className={classes.username}>{props.title}</Box>}
        </Box>
      </Box>
      <FlexSpacer maxHeight="8px" />
    </div>
  );
};
